import type { Task } from '../data/projectTypes'
import type { TestSession } from '../data/projectTypes'

export type InitialTestRunPhase = 'exploration' | 'discovering' | 'journey_testing' | 'done'

export type InitialTestRunStepState = 'completed' | 'active' | 'pending'

export type InitialTestRunStepId = 'exploration' | 'discovering' | 'journey_testing'

const PHASE_ORDER: InitialTestRunPhase[] = ['exploration', 'discovering', 'journey_testing', 'done']

const STEP_IDS: InitialTestRunStepId[] = ['exploration', 'discovering', 'journey_testing']

export function findInitialTestSession(sessions: TestSession[]): TestSession | null {
  if (sessions.length === 0) return null
  const ordinalOne = sessions.find((s) => s.ordinal === 1)
  if (ordinalOne) return ordinalOne
  return sessions.reduce<TestSession | null>(
    (best, s) => (!best || s.ordinal < best.ordinal ? s : best),
    null,
  )
}

export function explorationHost(targetUrl: string | null | undefined, projectName: string | null | undefined): string {
  if (targetUrl) {
    try {
      return new URL(targetUrl).host
    } catch {
      return targetUrl.replace(/^https?:\/\//, '').replace(/\/$/, '')
    }
  }
  return projectName?.trim() || 'your app'
}

// Generic over the two shapes that carry an exploration id + status: the project-wide Exploration
// rows (/api/explorations) and the explorations embedded in a test-sessions row — so a caller can
// pick the replay target from whichever response it already has in hand.
export function pickExplorationReplayId<T extends { id: string; status: string }>(explorations: T[]): string | null {
  const inProgress = explorations.find((e) => e.status === 'in-progress' || e.status === 'queued')
  if (inProgress) return inProgress.id
  return explorations[0]?.id ?? null
}

export function deriveInitialTestRunPhase(args: {
  expInProgress: number
  expCompleted: number
  expFailed: number
  expTotal: number
  journeyPending: number
  journeyTotal: number
}): InitialTestRunPhase {
  const expDone = args.expCompleted > 0 || args.expFailed > 0
  if (args.expInProgress > 0 || (args.expTotal > 0 && !expDone)) return 'exploration'
  if (args.journeyPending > 0) return 'journey_testing'
  if (expDone) return 'discovering'
  return 'exploration'
}

export function stepStateForPhase(stepId: InitialTestRunStepId, phase: InitialTestRunPhase): InitialTestRunStepState {
  if (phase === 'done') return 'completed'
  const stepIndex = STEP_IDS.indexOf(stepId)
  const phaseIndex = PHASE_ORDER.indexOf(phase)
  if (stepIndex < phaseIndex) return 'completed'
  if (stepIndex === phaseIndex) return 'active'
  return 'pending'
}

export function shouldShowInitialTestRunInterim(args: {
  initialSession: TestSession | null
  expTotal: number
  expCompleted: number
  expFailed: number
  // Whether QA has already surfaced something worth showing on the regular overview. Journeys start
  // being generated as soon as the exploration begins, so these can appear well before the initial
  // test session finishes.
  hasBugs: boolean
  hasJourneys: boolean
}): boolean {
  // As soon as there's real QA output to show (any journeys discovered or any bugs filed), drop the
  // interim setup screen and switch to the regular overview — even while the initial test session is
  // still running. The interim is only for the brief window before anything exists to display.
  if (args.hasBugs || args.hasJourneys) return false
  if (args.initialSession?.status === 'in-progress') return true
  if (args.initialSession?.status === 'completed') return false
  // Fallback before the session row exists (or legacy paths without sessions).
  return args.expTotal > 0 && args.expCompleted === 0 && args.expFailed === 0
}

function taskKindLabelSafe(task: Task): string {
  if (task.exploration_id) return 'Exploration'
  if (task.test_run_id) return 'Test Run'
  return 'Task'
}

export function currentTaskFromRunning(running: Task[]): { title: string; description: string } | null {
  const task = running[0]
  if (!task) return null
  const kind = taskKindLabelSafe(task)
  const title = task.journey_name?.trim() || task.goal?.trim() || kind
  const description = task.goal?.trim() || `${kind} in progress`
  return { title, description }
}
